import { Link } from 'react-router-dom';
import { BRAND } from '@/config/brand';
import LegalPageLayout, { formatAddress } from '@/components/layout/LegalPageLayout';

export default function Terms() {
  return (
    <LegalPageLayout title="Terms of Use">
      <section>
        <h2>1. Acceptance of terms</h2>
        <p>
          These Terms of Use (&ldquo;Terms&rdquo;) govern your access to and use of the websites, map search tools,
          accounts, and related services operated by {BRAND.legalName} (&ldquo;{BRAND.name},&rdquo; &ldquo;we,&rdquo;
          &ldquo;us,&rdquo; or &ldquo;our&rdquo;) (the &ldquo;Services&rdquo;). By accessing or using the Services, you
          agree to these Terms and our <Link to="/privacy">Privacy Policy</Link>. If you do not agree, do not use the
          Services.
        </p>
      </section>

      <section>
        <h2>2. Eligibility and accounts</h2>
        <p>
          You must be at least 18 years old and able to form a binding contract to use the Services. If you create an
          account, you agree to provide accurate information, keep credentials confidential, and accept responsibility for
          activity under your account. We may suspend or terminate accounts that violate these Terms or create risk to the
          Services or other users.
        </p>
      </section>

      <section>
        <h2>3. License to use the site</h2>
        <p>
          Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and
          use the Services for your personal, non-commercial purposes — for example, to identify properties you may be
          interested in buying or renting. You may not scrape, bulk-download, resell, republish, or commercially exploit
          listing data or other content from the Services without our prior written permission, except as expressly allowed
          by applicable MLS/IDX rules and these Terms.
        </p>
      </section>

      <section>
        <h2>4. IDX / MLS listing information disclaimer</h2>
        <p>
          IDX information is provided exclusively for consumers&apos; personal, non-commercial use and may not be used for
          any purpose other than to identify prospective properties consumers may be interested in purchasing. Information
          is deemed reliable but not guaranteed to be accurate. Buyers and renters should independently verify all listing
          details, including price, status, square footage, taxes, HOA dues, schools, and availability, with the listing
          broker, agent, or other authoritative sources before making decisions. Listing information may be updated on a
          periodic basis and may not reflect the most recent changes.
        </p>
        <p>
          {BRAND.name} is a technology platform and does not guarantee the completeness, accuracy, or availability of any
          listing. Nothing on the Services constitutes an offer, brokerage relationship, appraisal, or legal, tax, or
          financial advice unless expressly stated in a separate written agreement.
        </p>
      </section>

      <section>
        <h2>5. User content and conduct</h2>
        <p>When you submit content (messages, profiles, listings you control, reviews, or other materials), you represent that you have the rights to do so and that your content does not violate law or third-party rights. You agree not to:</p>
        <ul>
          <li>Use the Services for unlawful, fraudulent, harassing, or deceptive purposes;</li>
          <li>Interfere with security, scrape without authorization, or reverse engineer the Services;</li>
          <li>Misrepresent your identity, affiliation, or interest in a property;</li>
          <li>Post malware, spam, or content that infringes intellectual property or privacy rights;</li>
          <li>Use listing data in ways prohibited by MLS/IDX rules or these Terms.</li>
        </ul>
        <p>
          We may remove content or restrict access when we reasonably believe these Terms or applicable rules have been
          violated.
        </p>
      </section>

      <section>
        <h2>6. Agent / advertiser services and third-party links</h2>
        <p>
          The Services may help you find and contact real-estate professionals or view advertising placements. Agents,
          brokers, and advertisers are independent third parties. {BRAND.name} does not control their services, statements,
          or availability, and is not responsible for agreements you enter with them. The Services may also contain links
          to third-party websites; your use of those sites is governed by their terms and privacy policies.
        </p>
      </section>

      <section>
        <h2>7. Intellectual property</h2>
        <p>
          The {BRAND.name} name, logos, software, design, and original content are owned by {BRAND.legalName} or its
          licensors and are protected by intellectual property laws. Listing photos, remarks, and related MLS content remain
          subject to the rights of the listing broker, photographer, and data providers. Except for the limited license in
          Section 3, no rights are granted to you.
        </p>
      </section>

      <section>
        <h2>8. Disclaimers of warranties</h2>
        <p>
          THE SERVICES AND ALL CONTENT ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE.&rdquo; TO THE MAXIMUM
          EXTENT PERMITTED BY LAW, {BRAND.legalName.toUpperCase()} DISCLAIMS ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR
          STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT
          WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS, OR THAT LISTING DATA
          WILL BE ACCURATE OR CURRENT.
        </p>
      </section>

      <section>
        <h2>9. Limitation of liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, {BRAND.legalName.toUpperCase()} AND ITS OFFICERS, DIRECTORS, EMPLOYEES,
          AND AGENTS WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES,
          OR FOR LOST PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICES, EVEN IF ADVISED OF
          THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THE SERVICES WILL
          NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID US FOR THE SERVICES GIVING RISE TO THE CLAIM IN THE TWELVE (12)
          MONTHS BEFORE THE CLAIM OR (B) ONE HUNDRED U.S. DOLLARS (US $100).
        </p>
      </section>

      <section>
        <h2>10. Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless {BRAND.legalName} and its affiliates, officers, directors,
          employees, and agents from and against claims, damages, losses, and expenses (including reasonable attorneys&apos;
          fees) arising out of or related to your use of the Services, your content, your violation of these Terms, or your
          violation of any law or third-party right.
        </p>
      </section>

      <section>
        <h2>11. Governing law</h2>
        <p>
          These Terms are governed by the laws of the State of Florida, United States, without regard to conflict-of-law
          principles. Subject to Section 12, courts located in Palm Beach County, Florida (including those serving Boca
          Raton) will have exclusive jurisdiction over disputes arising from these Terms or the Services, and you consent
          to personal jurisdiction there.
        </p>
      </section>

      <section>
        <h2>12. Dispute resolution</h2>
        <p>
          Before filing a formal claim, you agree to contact us at{' '}
          <a href={`mailto:${BRAND.support.email}`}>{BRAND.support.email}</a> and attempt to resolve the dispute
          informally within thirty (30) days. If unresolved, either party may pursue claims in the state or federal courts
          located in Palm Beach County, Florida, unless applicable law requires otherwise. Nothing in these Terms prevents
          either party from seeking injunctive or other equitable relief for intellectual property or unauthorized use of
          the Services.
        </p>
      </section>

      <section>
        <h2>13. Changes and termination</h2>
        <p>
          We may modify these Terms by posting an updated version with a new effective date. Material changes will apply
          prospectively. We may suspend or discontinue the Services (in whole or part) at any time. Provisions that by
          their nature should survive (including disclaimers, limitations, indemnity, and governing law) will survive
          termination.
        </p>
      </section>

      <section>
        <h2>14. Contact</h2>
        <p>
          {BRAND.legalName}
          <br />
          {formatAddress()}
          <br />
          <a href={`mailto:${BRAND.support.email}`}>{BRAND.support.email}</a>
          {' · '}
          <Link to="/contact">Contact form</Link>
        </p>
      </section>
    </LegalPageLayout>
  );
}
